/**
 * EventCard - Renders a single event or note in the timeline
 */

import { ownerDocumentOf } from '../../../utils/ownerWindow';
import React from 'react';
import {
  Calculator,
  Edit3,
  Eraser,
  SlidersHorizontal,
  Trash2,
  MessageSquare,
  Circle,
  Clock,
  User,
  MoreVertical,
} from 'lucide-react';
import type { ForecastEvent, ForecastEventType } from '../../../api/events';
import { isAutoEvent, formatEventDescription } from '../../../api/events';

interface EventCardProps {
  event: ForecastEvent;
  onDelete?: (eventId: string) => void;
  isDeleting?: boolean;
  currentUserEmail?: string;
}

// Get the icon component for an event type
function getEventIcon(type: ForecastEventType) {
  switch (type) {
    case 'DRIVER_CHANGED':
      return Calculator;
    case 'OVERRIDE_CREATED':
    case 'OVERRIDE_UPDATED':
      return Edit3;
    case 'OVERRIDE_CLEARED':
      return Eraser;
    case 'ADJUSTMENT_CREATED':
    case 'ADJUSTMENT_UPDATED':
      return SlidersHorizontal;
    case 'ADJUSTMENT_DELETED':
      return Trash2;
    case 'NOTE':
      return MessageSquare;
    default:
      return Circle;
  }
}

// Get background color for event type
function getEventBgColor(type: ForecastEventType): string {
  if (type === 'NOTE') {
    return 'bg-slate-50 border-slate-200';
  }
  // Auto-logged events get amber tint
  return 'bg-amber-50 border-amber-200';
}

// Get icon color for event type - returns null for orange (handled via inline style)
function getIconColor(type: ForecastEventType): string | null {
  switch (type) {
    case 'NOTE':
      return 'text-slate-500';
    case 'DRIVER_CHANGED':
      return 'text-blue-500';
    case 'OVERRIDE_CREATED':
    case 'OVERRIDE_UPDATED':
      return 'text-purple-500';
    case 'OVERRIDE_CLEARED':
      return null; // use inline style for orange
    case 'ADJUSTMENT_CREATED':
    case 'ADJUSTMENT_UPDATED':
      return 'text-green-500';
    case 'ADJUSTMENT_DELETED':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
}

// Format relative time
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function EventCard({ event, onDelete, isDeleting, currentUserEmail }: EventCardProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Defensive: handle missing type
  const eventType = event.type || 'NOTE';
  const IconComponent = getEventIcon(eventType);
  const isAuto = isAutoEvent(eventType);
  const bgColor = getEventBgColor(eventType);
  const iconColor = getIconColor(eventType);
  const description = formatEventDescription(event);

  // Check if current user owns this event (can only delete own notes)
  const isOwnEvent = currentUserEmail && event.createdBy === currentUserEmail;
  const canDelete = !isAuto && isOwnEvent && onDelete;

  // Close menu on click outside
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) {
      const doc = ownerDocumentOf(menuRef.current);
      doc.addEventListener('mousedown', handleClickOutside);
      return () => doc.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleDelete = () => {
    if (onDelete && !isDeleting) {
      onDelete(event.id);
      setShowMenu(false);
    }
  };

  return (
    <div className={`rounded-lg border p-3 ${bgColor} relative group`}>
      {/* Header: Icon, Type badge, Menu */}
      <div className="flex items-start gap-2 mb-2">
        <div className={`p-1.5 rounded-md ${isAuto ? 'bg-amber-100' : 'bg-slate-100'}`}>
          <IconComponent
            className={`w-4 h-4 ${iconColor || ''}`}
            style={iconColor === null ? { color: 'var(--lodge-orange-400)' } : undefined}
          />
        </div>

        <div className="flex-1 min-w-0">
          {/* Type badge */}
          <div className="flex items-center gap-2">
            {isAuto && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                AUTO
              </span>
            )}
            <span className="text-xs font-medium text-slate-700">
              {eventType.replace(/_/g, ' ')}
            </span>
          </div>

          {/* User and time */}
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
            <span className="flex items-center gap-0.5">
              <User className="w-3 h-3" />
              {event.createdByName || event.createdBy}
            </span>
            <span className="flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              {formatRelativeTime(event.createdAt)}
            </span>
          </div>
        </div>

        {/* Menu button (only for user's own notes) */}
        {canDelete && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded hover:bg-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
              disabled={isDeleting}
            >
              <MoreVertical className="w-4 h-4 text-slate-400" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-10 min-w-[100px]">
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="text-sm text-slate-700 pl-8">
        {eventType === 'NOTE' ? (
          <p className="whitespace-pre-wrap">{event.data?.content}</p>
        ) : (
          <p>{description}</p>
        )}
      </div>

      {/* Month indicator (if month-specific) */}
      {event.month && (
        <div className="mt-2 pl-8">
          <span className="text-[10px] text-slate-400">
            Month: {new Date(2024, event.month - 1).toLocaleString('en-US', { month: 'short' })}
          </span>
        </div>
      )}
    </div>
  );
}

export default EventCard;
